import type { ValidationFile } from '../common/ValidationDialog/ValidationDialog';

const DB_NAME = 'mrs_uploads';
const DB_VERSION = 1;
const STORE_NAME = 'uploaded_files';
const RECORD_KEY = 'current';

export interface StoredUploadFile {
  name: string;
  size: number;
  type: string;
  blob: Blob;
}

export interface StoredUploadRecord {
  requestId: string;
  files: StoredUploadFile[];
  validationFiles: ValidationFile[];
}

const openDB = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this environment'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const withStore = async <T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest
): Promise<T> => {
  const db = await openDB();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, mode);
      const request = action(transaction.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => reject(request.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    db.close();
  }
};

export const saveUploadedFiles = async (record: StoredUploadRecord): Promise<void> => {
  await withStore('readwrite', (store) => store.put(record, RECORD_KEY));
};

export const getUploadedFiles = async (): Promise<StoredUploadRecord | null> => {
  try {
    const record = await withStore<StoredUploadRecord | undefined>('readonly', (store) =>
      store.get(RECORD_KEY)
    );
    return record ?? null;
  } catch (error) {
    console.error('Failed to read uploaded files from IndexedDB:', error);
    return null;
  }
};

export const clearUploadedFiles = async (): Promise<void> => {
  try {
    await withStore('readwrite', (store) => store.delete(RECORD_KEY));
  } catch (error) {
    console.error('Failed to clear uploaded files from IndexedDB:', error);
  }
};
